import {
  companiesService,
  Company,
  type PaymentMethodAsaas,
  type PaginationParams,
} from './api/companies.service';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { useApiProductsCatalogQuery } from '@/features/products/hooks/useApiProductsCatalogQuery';
import {
  AlertDialog,
  Button,
  Checkbox,
  ConfirmDialog,
  Dialog,
  Input,
  RadioGroup,
  Select,
  ManagedDataTable,
  MaskedInput,
  StatisticCard,
  Switch,
  SelectField,
  createSelectionColumn,
} from '@/design-system';
import { isCnpjFormat, isValidCnpj, MASKS, unmask, unmaskDocument } from '@/design-system/utils/masks';
import { type ColumnDef } from '@tanstack/react-table';
import { Archive, Building2, Eye, Minus, Plus, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import {
  companyDeletionConfirmation,
  companyDeletionsService,
  type CompanyDeletionMode,
} from '@/features/company-deletions';
import { useCompaniesListQuery } from './hooks/useCompaniesListQuery';
import { useCompanyStatsQuery } from './hooks/useCompanyStatsQuery';
import { useAssignablePlansQuery } from './hooks/useAssignablePlansQuery';
import { CompanyBulkMigrationDialog } from './components/CompanyBulkMigrationDialog';
import { CompanyCapacitySummary } from './components/CompanyCapacitySummary';
import { includedChannels } from './utils/included-channels';
import { AuthContext } from '@/core/auth/auth-context';
import { useContext } from 'react';

const subscriptionStatusLabel: Record<string, string> = {
  TRIALING: 'Em avaliação',
  ACTIVE: 'Ativo',
  PAST_DUE: 'Vencido',
  CANCELED: 'Cancelado',
  PENDING_PAYMENT: 'Pagamento pendente',
};

export function CompaniesPage() {
  const auth = useContext(AuthContext);
  const canMigrate = ['SUPERADMIN', 'FINANCE'].includes(auth?.user?.role ?? '');
  const [migrationSelection, setMigrationSelection] = useState<Company[] | null | undefined>(undefined);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const onErrorToast = useErrorToast();

  const [search, setSearch] = useState('');
  type CompanyListTab = 'active' | 'trash' | 'archive';
  const [listTab, setListTab] = useState<CompanyListTab>('active');
  const [subscriptionFilter, setSubscriptionFilter] = useState<NonNullable<PaginationParams['subscriptionStatus']> | ''>('');
  const includeDeleted = listTab !== 'active';
  const includePurged = listTab === 'archive';
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const { stats, isLoading: statsLoading, isError: statsError } = useCompanyStatsQuery();
  const [tableResetKey, setTableResetKey] = useState(0);
  const [showBulkEraseFinancialDialog, setShowBulkEraseFinancialDialog] = useState(false);
  const [bulkErasePhrase, setBulkErasePhrase] = useState('');
  const [bulkEraseIds, setBulkEraseIds] = useState<string[]>([]);
  const [bulkEraseLoading, setBulkEraseLoading] = useState(false);
  const [showBulkDestroyShellDialog, setShowBulkDestroyShellDialog] = useState(false);
  const [bulkDestroyPhrase, setBulkDestroyPhrase] = useState('');
  const [bulkDestroyIds, setBulkDestroyIds] = useState<string[]>([]);
  const [bulkDestroyLoading, setBulkDestroyLoading] = useState(false);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showHardDeleteDialog, setShowHardDeleteDialog] = useState(false);
  const [pendingDeletionMode, setPendingDeletionMode] = useState<CompanyDeletionMode | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const [createPaymentMethod, setCreatePaymentMethod] = useState<PaymentMethodAsaas>('BOLETO');
  const [alertPaymentUrl, setAlertPaymentUrl] = useState<string | null>(null);
  /** Credenciais do administrador recém-criadas pelo backend (uma vez só — staff salva). */
  const [ownerCredentials, setOwnerCredentials] = useState<{
    companyName: string;
    email: string;
    temporaryPassword: string;
    paymentUrl: string | null;
  } | null>(null);

  const emptyForm = () => ({
    name: '',
    fantasyName: '',
    slug: '',
    document: '',
    email: '',
    phone: '',
    mobilePhone: '',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    stateOrProvince: '',
    formattedAddress: '',
    placeId: '',
    country: 'BR',
    municipalInscription: '',
    stateInscription: '',
    defaultLanguage: 'pt-BR',
    timezone: 'America/Sao_Paulo',
    planId: '',
    seatCount: '1',
    enableTrial: false,
    trialDays: 14,
    // administrador da empresa — backend cria user + member administrador + dept default.
    // Senha opcional: se vazia, backend gera 12 chars base64 sem ambíguos
    // e retorna no response (UMA VEZ — staff precisa salvar).
    ownerEmail: '',
    ownerName: '',
    ownerPassword: '',
    informPassword: false,
    ownerPasswordConfirmation: '',
    additionalConnectionCount: '0',
  });

  const [formData, setFormData] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingCnpj, setIsFetchingCnpj] = useState(false);
  const plansQuery = useAssignablePlansQuery(showCreateDialog);
  const basePlans = plansQuery.data?.data ?? [];
  const selectedPlan = basePlans.find((plan) => plan.id === formData.planId);
  const selectedPlanIncluded = includedChannels(selectedPlan?.limits);
  const formatPlanAmount = (amount: number, currency?: string) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency ?? selectedPlan?.currency ?? 'BRL' }).format(amount / 100);
  const createSeatsValid = Number.isInteger(Number(formData.seatCount)) && Number(formData.seatCount) >= 1;
  const createChannelsValid = formData.additionalConnectionCount.trim() !== '' && Number.isInteger(Number(formData.additionalConnectionCount)) && Number(formData.additionalConnectionCount) >= 0;
  const productsQuery = useApiProductsCatalogQuery(showCreateDialog);
  const apiProducts = (productsQuery.data ?? []).filter((product) => product.active && product.featureKey === 'allow_api' && product.isModule && product.billingType === 'RECURRING');
  const [apiProductId, setApiProductId] = useState('');
  const selectedApiProduct = apiProducts.find((product) => product.id === apiProductId);
  useEffect(() => { if (showCreateDialog) setApiProductId(''); }, [showCreateDialog]);

  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const listFilters = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      includeDeleted,
      includePurged: includeDeleted ? includePurged : false,
      subscriptionStatus: subscriptionFilter || undefined,
    }),
    [page, limit, debouncedSearch, includeDeleted, includePurged, subscriptionFilter]
  );

  const { data: companiesData, isLoading, isError, error } = useCompaniesListQuery(listFilters);
  const companies = companiesData?.data ?? [];
  const totalPages = companiesData?.pagination.totalPages ?? 1;
  const total = companiesData?.pagination.total ?? 0;

  useEffect(() => {
    if (isError && error) onErrorToast(error, 'Erro ao carregar empresas');
  }, [isError, error, onErrorToast]);

  const invalidateCompanyQueries = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, listTab, subscriptionFilter]);

  useEffect(() => {
    if (showCreateDialog) {
      setCreatePaymentMethod('BOLETO');
    }
  }, [showCreateDialog]);

  const validateForm = (scope: 'company' | 'plan' | 'all' = 'all') => {
    const errors: Record<string, string> = {};
    if (scope !== 'plan' && !formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    } else if (scope !== 'plan' && formData.name.length < 2) {
      errors.name = 'Nome deve ter pelo menos 2 caracteres';
    }
    if (scope !== 'plan' && unmaskDocument(formData.document).length > 0 && !isCnpjFormat(formData.document)) {
      errors.document = 'O CNPJ deve ter 14 caracteres (12 letras ou números e 2 dígitos verificadores).';
    } else if (
      scope !== 'plan' &&
      unmaskDocument(formData.document).length > 0 &&
      // Empresas legadas com documento inválido continuam editáveis: só revalida quando o CNPJ muda.
      (showCreateDialog || unmaskDocument(formData.document) !== unmaskDocument(selectedCompany?.document ?? '')) &&
      !isValidCnpj(formData.document)
    ) {
      errors.document = 'CNPJ inválido: o dígito verificador não confere.';
    }
    if (scope !== 'company' && showCreateDialog && !formData.planId) {
      errors.planId = 'Selecione um plano';
    }
    if (scope !== 'company' && showCreateDialog && (!Number.isInteger(Number(formData.seatCount)) || Number(formData.seatCount) < 1 || Number(formData.seatCount) > 100000)) {
      errors.seatCount = 'Informe entre 1 e 100000 licenças.';
    }
    if (showCreateDialog && scope !== 'plan') {
      const emailTrim = formData.ownerEmail.trim();
      if (!emailTrim) {
        errors.ownerEmail = 'Email do administrador é obrigatório';
      } else if (!emailTrim.includes('@') || !emailTrim.includes('.')) {
        errors.ownerEmail = 'Email do administrador inválido';
      }
      const nameTrim = formData.ownerName.trim();
      if (!nameTrim || nameTrim.length < 2) {
        errors.ownerName = 'Nome do administrador deve ter pelo menos 2 caracteres';
      }
      if (formData.informPassword && formData.ownerPassword.trim().length < 8) errors.ownerPassword = 'Informe uma senha com pelo menos 8 caracteres.';
      if (formData.informPassword && formData.ownerPassword !== formData.ownerPasswordConfirmation) errors.ownerPasswordConfirmation = 'As senhas devem ser iguais.';

    }
    if (showCreateDialog && scope !== 'company') {
      if (apiProductId && (!selectedApiProduct || selectedPlan?.currency !== 'BRL')) errors.apiProduct = 'Revise o produto de API selecionado.';
      const connections = Number(formData.additionalConnectionCount);
      if (!Number.isInteger(connections) || connections < 0 || connections > 100000) errors.additionalConnectionCount = 'Informe entre 0 e 100000 canais adicionais.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleLookupCnpj = async () => {
    if (!isCnpjFormat(formData.document)) {
      setAlertMessage('Informe um CNPJ válido (14 caracteres) para consultar.');
      setShowAlert(true);
      return;
    }
    setIsFetchingCnpj(true);
    try {
      const { exists } = await companiesService.checkDocument(formData.document);
      if (exists) {
        setAlertMessage('Esta empresa já está cadastrada. O CNPJ já está em uso.');
        setShowAlert(true);
        return;
      }
      const data = await companiesService.cnpjLookup(formData.document);
      setFormData((prev) => ({
        ...prev,
        name: data.name || prev.name,
        fantasyName: data.fantasyName || prev.fantasyName,
        document: data.document || prev.document,
        email: data.email || prev.email,
        phone: data.phone || prev.phone,
        addressLine1: data.addressLine1 || prev.addressLine1,
        addressLine2: data.addressLine2 || prev.addressLine2,
        postalCode: data.postalCode || prev.postalCode,
        city: data.city || prev.city,
        stateOrProvince: data.stateOrProvince || prev.stateOrProvince,
        formattedAddress: data.formattedAddress || prev.formattedAddress,
        placeId: data.placeId || prev.placeId,
        slug: prev.slug || generateSlug(data.fantasyName || data.name || prev.name),
      }));
      setAlertMessage('Dados do CNPJ carregados com sucesso!');
      setShowAlert(true);
    } catch (e: unknown) {
      setAlertMessage(e instanceof Error ? e.message : 'Erro ao consultar CNPJ. Verifique o número e tente novamente.');
      setShowAlert(true);
    } finally {
      setIsFetchingCnpj(false);
    }
  };

  const fetchAddressByCep = async (cep: string) => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setFormData((prev) => ({
          ...prev,
          addressLine1: data.logradouro || prev.addressLine1,
          addressLine2: data.bairro || prev.addressLine2,
          city: data.localidade || prev.city,
          stateOrProvince: data.uf || prev.stateOrProvince,
        }));
      }
    } catch {
      // silent
    }
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    if (isCnpjFormat(formData.document)) {
      try {
        const { exists } = await companiesService.checkDocument(formData.document);
        if (exists) {
          setAlertMessage('Esta empresa já está cadastrada. O CNPJ já está em uso.');
          setShowAlert(true);
          return;
        }
      } catch {
        // proceed to create; backend also validates
      }
    }

    setIsSubmitting(true);
    setAlertPaymentUrl(null);
    try {
      const result = await companiesService.create({
        name: formData.name,
        fantasyName: formData.fantasyName || undefined,
        slug: generateSlug(formData.name),
        document: formData.document || undefined,
        country: formData.country || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        mobilePhone: formData.mobilePhone || undefined,
        addressLine1: formData.addressLine1 || undefined,
        addressLine2: formData.addressLine2 || undefined,
        postalCode: formData.postalCode || undefined,
        city: formData.city || undefined,
        stateOrProvince: formData.stateOrProvince || undefined,
        formattedAddress: formData.formattedAddress || undefined,
        placeId: formData.placeId || undefined,
        municipalInscription: formData.municipalInscription || undefined,
        stateInscription: formData.stateInscription || undefined,
        planId: formData.planId,
        seatCount: Number(formData.seatCount),
        licensingCurrency: basePlans.find((plan) => plan.id === formData.planId)?.currency ?? 'BRL',
        trialDays: formData.enableTrial ? formData.trialDays : 0,
        additionalConnectionCount: Number(formData.additionalConnectionCount),
        paymentMethod: createPaymentMethod,
        ...(selectedApiProduct ? { items: [{ productId: selectedApiProduct.id, quantity: 1 }] } : {}),
        ownerEmail: formData.ownerEmail.trim(),
        ownerName: formData.ownerName.trim(),
        ownerPassword: formData.informPassword ? formData.ownerPassword.trim() : undefined,
      });

      setShowCreateDialog(false);
      setFormData(emptyForm());
      setFormErrors({});
      setCreatePaymentMethod('BOLETO');
      void invalidateCompanyQueries();
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });

      // Mostra credenciais do administrador em modal nao-fechavel-acidentalmente.
      // Staff PRECISA salvar — backend nao expoe a senha depois.
      if (result.owner) {
        setOwnerCredentials({
          companyName: result.company?.name ?? formData.name,
          email: result.owner.email,
          temporaryPassword: result.owner.temporaryPassword,
          paymentUrl: result.paymentUrl ?? null,
        });
      } else if (result.paymentUrl) {
        setAlertMessage(
          'Empresa criada com sucesso! Clique em "Ir para pagamento" para abrir o link do Asaas (PIX, Boleto ou Cartão).'
        );
        setAlertPaymentUrl(result.paymentUrl);
        setShowAlert(true);
      } else {
        setAlertMessage('Empresa criada com sucesso!');
        setShowAlert(true);
      }
    } catch (error: unknown) {
      setAlertMessage(error instanceof Error ? error.message : 'Erro ao criar empresa');
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedCompany || !validateForm()) return;

    setIsSubmitting(true);
    try {
      await companiesService.update(selectedCompany.id, {
        name: formData.name,
        fantasyName: formData.fantasyName || undefined,
        slug: formData.slug || generateSlug(formData.name),
        document: formData.document.trim() || undefined,
        country: formData.country.trim() || undefined,
        email: formData.email.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        mobilePhone: formData.mobilePhone.trim() || undefined,
        addressLine1: formData.addressLine1.trim() || undefined,
        addressLine2: formData.addressLine2.trim() || undefined,
        postalCode: formData.postalCode.trim() || undefined,
        city: formData.city.trim() || undefined,
        stateOrProvince: formData.stateOrProvince.trim() || undefined,
        formattedAddress: formData.formattedAddress.trim() || undefined,
        placeId: formData.placeId.trim() || undefined,
        municipalInscription: formData.municipalInscription.trim() || undefined,
        stateInscription: formData.stateInscription.trim() || undefined,
        defaultLanguage: formData.defaultLanguage,
        timezone: formData.timezone,
      });
      setShowEditDialog(false);
      setSelectedCompany(null);
      setFormData(emptyForm());
      setFormErrors({});
      invalidateCompanyQueries();
      setAlertMessage('Empresa atualizada com sucesso!');
      setShowAlert(true);
    } catch (error: unknown) {
      setAlertMessage(error instanceof Error ? error.message : 'Erro ao atualizar empresa');
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Primeira etapa: o usuário escolhe o tipo; a exclusão só é solicitada após a segunda confirmação. */
  const handleRequestDeletion = async () => {
    if (!selectedCompany || !pendingDeletionMode) return;

    setIsSubmitting(true);
    try {
      await companyDeletionsService.request(selectedCompany.id, pendingDeletionMode);
      setPendingDeletionMode(null);
      setShowDeleteDialog(false);
      setSelectedCompany(null);
      void invalidateCompanyQueries();
      void queryClient.invalidateQueries({ queryKey: queryKeys.companyDeletions.all });
      setAlertMessage('Exclusão solicitada. Ela será processada em segundo plano; acompanhe em Ferramentas › Exclusões de empresas.');
      setShowAlert(true);
    } catch (error: unknown) {
      setPendingDeletionMode(null);
      setAlertMessage(error instanceof Error ? error.message : 'Erro ao solicitar exclusão da empresa');
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHardDelete = async () => {
    if (!selectedCompany) return;

    setIsSubmitting(true);
    try {
      await companyDeletionsService.request(selectedCompany.id, 'DELETE_ALL_DATA');
      setShowHardDeleteDialog(false);
      setSelectedCompany(null);
      void invalidateCompanyQueries();
      void queryClient.invalidateQueries({ queryKey: queryKeys.companyDeletions.all });
      setAlertMessage('Exclusão solicitada. Ela será processada em segundo plano; acompanhe em Ferramentas › Exclusões de empresas.');
      setShowAlert(true);
    } catch (error: unknown) {
      setAlertMessage(error instanceof Error ? error.message : 'Erro ao solicitar exclusão da empresa');
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const companyColumns = useMemo<ColumnDef<Company, unknown>[]>(() => {
    const cols: ColumnDef<Company, unknown>[] = [
      {
        accessorKey: 'name',
        header: 'Nome',
        size: 240,
        cell: ({ row }) => (
          <div className="max-w-[220px] break-words text-sm font-medium text-gray-900">{row.original.name}</div>
        ),
      },
      {
        accessorKey: 'document',
        header: 'Documento',
        size: 180,
        minSize: 170,
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-sm text-gray-600">{row.original.document ?? '—'}</div>
        ),
      },
      {
        accessorKey: 'slug',
        header: 'Slug',
        cell: ({ row }) => (
          <div className="text-sm text-gray-500">{row.original.slug}</div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const company = row.original;
          const isDeleted = !!company.deletedAt;
          const status = (company as { status?: string }).status;
          if (status === 'PURGED') {
            return (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                Purgado (arquivo)
              </span>
            );
          }
          if (isDeleted) {
            return (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                Arquivada
              </span>
            );
          }
          return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              Ativa
            </span>
          );
        },
      },
      {
        id: 'subscriptionStatus',
        header: 'Assinatura',
        size: 160,
        minSize: 150,
        cell: ({ row }) => {
          const company = row.original;
          const status = company.subscriptionStatus ?? (company.hasActiveSubscription ? 'ACTIVE' : null);
          const cls =
            company.subscriptionStatus === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : company.subscriptionStatus === 'TRIALING'
                ? 'bg-amber-100 text-amber-800'
                : company.subscriptionStatus === 'PAST_DUE'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800';
          return (
            <span className={`inline-flex whitespace-nowrap px-2 py-1 text-xs font-medium rounded-full ${cls}`}>
              {status ? subscriptionStatusLabel[status] ?? status : 'Inativo'}
            </span>
          );
        },
      },
      {
        accessorKey: 'timezone',
        header: 'Timezone',
        cell: ({ row }) => (
          <div className="text-xs text-gray-500">{row.original.timezone ?? '—'}</div>
        ),
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Ações</div>,
        cell: ({ row }) => {
          const company = row.original;
          return (
            <div
              className="flex items-center justify-end gap-2"
              onClick={(e) => e.stopPropagation()}
            >
                <Button variant="ghost" size="sm" type="button" aria-label="Detalhes"
                  onClick={() => navigate(`/companies/${company.id}/overview`)}
                >
                  <Eye size={16} />Detalhes
                </Button>
            </div>
          );
        },
      },
    ];
    return listTab === 'archive' || (listTab === 'active' && canMigrate) ? [createSelectionColumn<Company>(), ...cols] : cols;
  }, [listTab, navigate, canMigrate]);

  const handleBulkEraseFinancialSubmit = async () => {
    const phrase = bulkErasePhrase.trim();
    if (phrase !== 'APAGAR HISTORICO') {
      setAlertMessage('Digite exatamente: APAGAR HISTORICO');
      setShowAlert(true);
      return;
    }
    if (bulkEraseIds.length === 0) return;
    setBulkEraseLoading(true);
    try {
      const results = await companiesService.bulkEraseFinancialHistory(bulkEraseIds, 'APAGAR HISTORICO');
      const ok = results.filter((r) => r.ok).length;
      const failed = results.filter((r) => !r.ok);
      setShowBulkEraseFinancialDialog(false);
      setBulkErasePhrase('');
      setBulkEraseIds([]);
      await invalidateCompanyQueries();
      setTableResetKey((k) => k + 1);
      setAlertMessage(
        failed.length > 0
          ? `Lote finalizado: ${ok} com sucesso, ${failed.length} ignoradas ou com erro (só contas PURGED no arquivo).`
          : `Histórico financeiro removido no Orya para ${ok} empresa(s). Você continua nesta tela.`
      );
      setShowAlert(true);
    } catch (e: unknown) {
      setAlertMessage(e instanceof Error ? e.message : 'Erro ao processar lote');
      setShowAlert(true);
    } finally {
      setBulkEraseLoading(false);
    }
  };

  const handleBulkDestroyShellSubmit = async () => {
    const phrase = bulkDestroyPhrase.trim();
    if (phrase !== 'EXCLUIR SEM RASTRO') {
      setAlertMessage('Digite exatamente: EXCLUIR SEM RASTRO');
      setShowAlert(true);
      return;
    }
    if (bulkDestroyIds.length === 0) return;
    setBulkDestroyLoading(true);
    try {
      const results = await companiesService.bulkDestroyPurgedShell(bulkDestroyIds, 'EXCLUIR SEM RASTRO');
      const ok = results.filter((r) => r.ok).length;
      const failed = results.filter((r) => !r.ok);
      setShowBulkDestroyShellDialog(false);
      setBulkDestroyPhrase('');
      setBulkDestroyIds([]);
      await invalidateCompanyQueries();
      setTableResetKey((k) => k + 1);
      setAlertMessage(
        failed.length > 0
          ? `Lote finalizado: ${ok} shell(s) removida(s), ${failed.length} ignoradas ou com erro (só PURGED).`
          : `${ok} empresa(s) removida(s) do Orya (sem linha local). Gateways externos não foram alterados.`
      );
      setShowAlert(true);
    } catch (e: unknown) {
      setAlertMessage(e instanceof Error ? e.message : 'Erro ao processar lote');
      setShowAlert(true);
    } finally {
      setBulkDestroyLoading(false);
    }
  };

  return (
    <DashboardLayout title="Empresas">
      <div className="space-y-6">
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatisticCard
              title="Empresas ativas"
              value={statsError ? null : stats.active}
              icon={Building2}
              tone="success"
              loading={statsLoading}
            />
            <StatisticCard
              title="Arquivadas (restauráveis)"
              value={statsError ? null : stats.trash}
              icon={Trash2}
              tone="neutral"
              loading={statsLoading}
            />
            <StatisticCard
              title="Arquivo financeiro"
              value={statsError ? null : stats.archive}
              icon={Archive}
              tone="warning"
              loading={statsLoading}
            />
          </div>
        </div>

        <div className="space-y-4">
          {listTab === 'archive' ? (
            <p className="text-sm text-gray-600">
              Apenas empresas que ainda têm <strong>assinatura</strong> ou <strong>referência de gateway</strong> no
              Orya aparecem aqui. Depois de apagar o histórico financeiro (detalhe ou lote), a linha some desta lista
              (a shell pode permanecer no banco até você usar &quot;Excluir shell&quot; no detalhe). Selecione linhas
              para ação em lote.
            </p>
          ) : null}
          <ManagedDataTable<Company, unknown>
            title="Lista de Empresas"
            key={`empresas-${listTab}-${page}-${tableResetKey}`}
            columns={companyColumns}
            data={companies}
            searchPlaceholder="Buscar por nome ou slug..."
            searchValue={search}
            onSearchChange={setSearch}
            toolbarFilters={<>
              <div className="w-40"><SelectField label="Status" value={listTab} onChange={(value) => { setListTab(value as CompanyListTab); setPage(1); }} options={[{ value: 'active', label: 'Ativa' }, { value: 'trash', label: 'Arquivada' }, { value: 'archive', label: 'Arquivo financeiro' }]} /></div>
              <div className="w-48"><SelectField label="Assinatura" value={subscriptionFilter} onChange={(value) => { setSubscriptionFilter(value as typeof subscriptionFilter); setPage(1); }} options={[{ value: '', label: 'Todas as assinaturas' }, ...Object.entries(subscriptionStatusLabel).map(([value, label]) => ({ value, label })), { value: 'NONE', label: 'Sem assinatura' }]} /></div>
            </>}
            loading={isLoading}
            enableRowSelection={listTab === 'archive' || (listTab === 'active' && canMigrate)}
            bulkActions={
              listTab === 'archive'
                ? [
                    {
                      label: 'Apagar histórico financeiro (lote)',
                      variant: 'danger',
                      onClick: (rows) => {
                        setBulkEraseIds(rows.map((c) => c.id));
                        setBulkErasePhrase('');
                        setShowBulkEraseFinancialDialog(true);
                      },
                    },
                    {
                      label: 'Excluir shells do Orya (lote)',
                      variant: 'danger',
                      onClick: (rows) => {
                        setBulkDestroyIds(rows.map((c) => c.id));
                        setBulkDestroyPhrase('');
                        setShowBulkDestroyShellDialog(true);
                      },
                    },
                  ]
                : listTab === 'active' && canMigrate ? [{ label: 'Migrar selecionadas', variant: 'primary', onClick: (rows) => setMigrationSelection(rows) }] : []
            }
            onRowClick={(company) => navigate(`/companies/${company.id}/overview`)}
            pagination={{
              page,
              totalPages,
              total,
              limit,
              onPageChange: setPage,
            }}
            toolbarButtons={[
              ...(listTab === 'active' && canMigrate ? [{ label: 'Migrar clientes', variant: 'secondary' as const, onClick: () => setMigrationSelection(null) }] : []),
              {
                label: 'Nova empresa',
                icon: <Plus size={18} />,
                onClick: () => {
                  setFormData(emptyForm());
                  setFormErrors({});
                  setCreateStep(0);
                  setShowCreateDialog(true);
                },
                variant: 'primary',
              },
            ]}
            emptyMessage="Nenhuma empresa encontrada."
          />
        </div>
      </div>

      {/* Create Dialog */}
      {migrationSelection !== undefined && canMigrate ? <CompanyBulkMigrationDialog selected={migrationSelection ?? undefined} onClose={() => setMigrationSelection(undefined)} onSaved={async () => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
        await queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });
      }} /> : null}
      <Dialog
        isOpen={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setFormData(emptyForm());
          setFormErrors({});
        }}
        title="Nova empresa"
        size="lg"
        footer={(
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => { setShowCreateDialog(false); setFormData(emptyForm()); setFormErrors({}); }} disabled={isSubmitting}>Cancelar</Button>
            {createStep > 0 && <Button variant="subtle" onClick={() => setCreateStep((step) => step - 1)} disabled={isSubmitting}>Voltar</Button>}
            {createStep < 2 ? <Button onClick={() => { if (validateForm(createStep === 0 ? 'company' : 'plan')) setCreateStep((step) => step + 1); }} disabled={createStep === 1 && (plansQuery.isLoading || plansQuery.isError || !selectedPlan)}>Continuar</Button> : <Button variant="primary" onClick={handleCreate} isLoading={isSubmitting} disabled={!selectedPlan}>Criar Empresa</Button>}
          </div>
        )}
      >
        <div className="space-y-6">
          <ol aria-label="Etapas do cadastro" className="grid grid-cols-3 gap-2 border-b border-gray-200 pb-4">
            {['Empresa e administrador', 'Plano e adicionais', 'Revisão'].map((label, index) => <li key={label} aria-current={createStep === index ? 'step' : undefined} className={createStep === index ? 'text-sm font-semibold text-primary' : 'text-sm text-gray-500'}>{index + 1}. {label}</li>)}
          </ol>
          {createStep === 0 && <>          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Dados da empresa</h3>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <MaskedInput
                  label="CNPJ"
                  mask={MASKS.CNPJ}
                  value={formData.document}
                  onChange={(e) => setFormData((prev) => ({ ...prev, document: e.target.value }))}
                  placeholder="00.000.000/0001-00" aria-describedby="company-cnpj-hint" error={formErrors.document}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={handleLookupCnpj}
                disabled={!isCnpjFormat(formData.document)}
                isLoading={isFetchingCnpj}
              >
                Buscar
              </Button>
            </div>
            <p id="company-cnpj-hint" className="mt-1.5 text-xs text-gray-500">Informe o CNPJ (numérico ou alfanumérico) e clique em Buscar para preencher os dados automaticamente.</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Nome (Razão Social)"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                error={formErrors.name}
                required
                placeholder="Preenchido pela busca de CNPJ ou informe manualmente"
              />
              <Input
                label="Nome Fantasia"
                value={formData.fantasyName}
                onChange={(e) => setFormData((prev) => ({ ...prev, fantasyName: e.target.value }))}
                placeholder="Nome comercial"
              />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Contato e endereço da empresa</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Para cobrança"
              />
              <MaskedInput
                label="Telefone"
                mask={MASKS.PHONE}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(11) 3333-4444"
              />
              <MaskedInput
                label="Celular"
                mask={MASKS.CELL}
                value={formData.mobilePhone}
                onChange={(e) => setFormData({ ...formData, mobilePhone: e.target.value })}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MaskedInput
                  label="CEP"
                  mask={MASKS.CEP}
                  value={formData.postalCode}
                  onChange={(e) => {
                    setFormData({ ...formData, postalCode: e.target.value });
                    if (unmask(e.target.value).length === 8) fetchAddressByCep(e.target.value);
                  }}
                  placeholder="00000-000"
                />
                <Input
                  label="Endereço"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  placeholder="Logradouro, número"
                />
                <Input
                  label="Complemento / bairro"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  placeholder="Complemento / bairro"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  label="Cidade"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
                <Input
                  label="Estado / Província"
                  value={formData.stateOrProvince}
                  onChange={(e) => setFormData({ ...formData, stateOrProvince: e.target.value })}
                  placeholder="SP"
                />
                <Input
                  label="País (ISO)"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase().slice(0, 2) })}
                  placeholder="BR"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Inscrição municipal"
                  value={formData.municipalInscription}
                  onChange={(e) => setFormData({ ...formData, municipalInscription: e.target.value })}
                  placeholder="Opcional"
                />
                <Input
                  label="Inscrição estadual"
                  value={formData.stateInscription}
                  onChange={(e) => setFormData({ ...formData, stateInscription: e.target.value })}
                  placeholder="Opcional"
                />
              </div>
            </div>
          </div>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Conta do administrador</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Email do administrador" type="email" required value={formData.ownerEmail} error={formErrors.ownerEmail} onChange={(e) => setFormData((prev) => ({ ...prev, ownerEmail: e.target.value }))} />
              <Input label="Nome do administrador" required maxLength={255} value={formData.ownerName} error={formErrors.ownerName} onChange={(e) => setFormData((prev) => ({ ...prev, ownerName: e.target.value }))} />
            </div>
            <Switch label="Informar senha" checked={formData.informPassword} onChange={(checked) => setFormData((prev) => ({ ...prev, informPassword: checked, ownerPassword: '', ownerPasswordConfirmation: '' }))} />
            {formData.informPassword ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input label="Senha" type="password" autoComplete="new-password" maxLength={128} required value={formData.ownerPassword} error={formErrors.ownerPassword} onChange={(e) => setFormData((prev) => ({ ...prev, ownerPassword: e.target.value }))} />
              <Input label="Confirmação de senha" type="password" autoComplete="new-password" maxLength={128} required value={formData.ownerPasswordConfirmation} error={formErrors.ownerPasswordConfirmation} onChange={(e) => setFormData((prev) => ({ ...prev, ownerPasswordConfirmation: e.target.value }))} />
            </div> : <p className="text-xs text-gray-500">Uma senha será gerada e exibida após criar a empresa.</p>}
          </section>
</>}
          {createStep === 1 && <>          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Plano</h3>
            <p className="text-xs text-gray-500 mb-3">A assinatura será criada automaticamente. Empresas não podem existir sem um plano.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select label="Plano *"
                  value={formData.planId}
                  onChange={(e) => { setFormData((prev) => ({ ...prev, planId: e.target.value })); if (basePlans.find((plan) => plan.id === e.target.value)?.currency !== 'BRL') setApiProductId(''); }}
                  error={formErrors.planId}
                >
                  <option value="">
                    {plansQuery.isLoading ? 'Carregando planos...' : 'Selecione um plano'}
                  </option>
                  {basePlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: p.currency }).format(p.price / 100)} por licença
                    </option>
                  ))}
                </Select>
              <Input label="Licenças simultâneas *" type="number" min={1} max={100000} value={formData.seatCount}
                onChange={(event) => setFormData((prev) => ({ ...prev, seatCount: event.target.value }))}
                error={!createSeatsValid ? 'Informe uma quantidade inteira de, no mínimo, 1 licença.' : undefined} />
              </div>
              {plansQuery.isError ? <div role="alert" className="text-sm text-red-600">Não foi possível carregar os planos. <Button variant="subtle" onClick={() => void plansQuery.refetch()}>Tentar novamente</Button></div> : !plansQuery.isLoading && basePlans.length === 0 ? <p role="status" className="text-sm text-amber-700">Nenhum plano ativo e configurado disponível. Ative um plano com módulos e preço em Planos.</p> : null}
              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/50">
                <Checkbox
                  label="Habilitar período de trial"
                  checked={formData.enableTrial}
                  onChange={(e) => setFormData((prev) => ({ ...prev, enableTrial: e.target.checked }))}
                />
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Define se a empresa terá trial. Sem marcar, a primeira cobrança ocorre imediatamente.
                </p>
                {formData.enableTrial && (
                  <div className="mt-3 ml-6">
                    <label className="block text-sm font-medium text-gray-600 mb-1.5">Duração do trial</label>
                    <Select
                      value={String(formData.trialDays)}
                      onChange={(e) => setFormData((prev) => ({ ...prev, trialDays: parseInt(e.target.value, 10) }))}
                      className="w-full max-w-[140px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="14">14 dias</option>
                      <option value="30">30 dias</option>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Adicionais</h3>
            {productsQuery.isError ? <div role="alert" className="text-sm text-red-600">Não foi possível carregar os produtos. <Button variant="subtle" onClick={() => void productsQuery.refetch()}>Tentar novamente</Button></div> : <SelectField label="API" value={apiProductId} onChange={setApiProductId} disabled={productsQuery.isLoading || !selectedPlan || selectedPlan.currency !== 'BRL'} options={[
              { value: '', label: productsQuery.isLoading ? 'Carregando produtos…' : 'Não contratar API' },
              ...apiProducts.map((product) => ({ value: product.id, label: `${product.name} · ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(product.price / 100)} ${selectedPlan?.interval === 'year' ? 'por ano' : selectedPlan?.interval === 'semester' ? 'por semestre' : 'por mês'}` })),
            ]} />}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-3">
              <span className="text-sm font-medium">Canais adicionais</span>
              <span className="text-sm text-gray-500">{selectedPlan ? `${formatPlanAmount(selectedPlan.price)} cada (valor de 1 licença) · ${selectedPlanIncluded} ${selectedPlanIncluded === 1 ? 'canal já incluído' : 'canais já incluídos'} no plano` : 'Selecione um plano'}</span>
              <div className="flex items-center gap-2">
                <Button variant="subtle" aria-label="Diminuir Canais adicionais" disabled={Number(formData.additionalConnectionCount) <= 0} onClick={() => setFormData((prev) => ({ ...prev, additionalConnectionCount: String(Math.max(0, Number(prev.additionalConnectionCount) - 1)) }))}><Minus className="h-4 w-4" /></Button>
                <Input aria-label="Canais adicionais" className="w-16 text-center" type="number" min={0} max={100000} value={formData.additionalConnectionCount} onChange={(e) => setFormData((prev) => ({ ...prev, additionalConnectionCount: e.target.value }))} />
                <Button variant="subtle" aria-label="Aumentar Canais adicionais" disabled={Number(formData.additionalConnectionCount) >= 100000} onClick={() => setFormData((prev) => ({ ...prev, additionalConnectionCount: String(Number(prev.additionalConnectionCount) + 1) }))}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            {formErrors.additionalConnectionCount && <p role="alert" className="text-sm text-red-600">{formErrors.additionalConnectionCount}</p>}
            {selectedPlan && createSeatsValid && createChannelsValid ? <CompanyCapacitySummary seats={Number(formData.seatCount)} included={selectedPlanIncluded} additional={Number(formData.additionalConnectionCount)}
              unitAmount={selectedPlan.price} currency={selectedPlan.currency} extraAmount={selectedApiProduct?.price ?? 0} formatPrice={formatPlanAmount} /> : null}
            {formErrors.apiProduct && <p role="alert" className="text-sm text-red-600">{formErrors.apiProduct}</p>}
          </section>
          <div>
            <RadioGroup
              label="Cobrança"
              value={createPaymentMethod}
              onChange={(value) => setCreatePaymentMethod(value as typeof createPaymentMethod)}
              options={[
                { value: 'MANUAL', label: 'Manual' },
                { value: 'BOLETO', label: 'Boleto' },
                { value: 'PIX', label: 'PIX' },
                { value: 'CREDIT_CARD', label: 'Cartão de crédito' },
              ]}
            />
            <input type="hidden" name="createPaymentMethod" value={createPaymentMethod} />
            {createPaymentMethod === 'MANUAL' && <p className="mt-2 text-xs text-gray-500">A fatura será interna, sem envio ao gateway. O financeiro poderá registrar o pagamento manualmente.</p>}
          </div>

</>}
          {createStep === 2 && <>          <section className="space-y-4">
            <div className="space-y-1"><h3 className="text-sm font-semibold text-gray-700">Revisão do cadastro</h3><p className="text-xs text-gray-500">Confira os dados antes de criar a empresa.</p></div>
            <dl className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-slate-50 p-4 sm:grid-cols-2">
              {[['Razão social', formData.name], ['Nome fantasia', formData.fantasyName || '—'], ['CNPJ', formData.document || '—'], ['Administrador', formData.ownerName], ['Email do administrador', formData.ownerEmail], ['Senha', formData.informPassword ? 'Informada (não exibida)' : 'Gerada automaticamente'], ['Plano', selectedPlan?.name ?? '—'], ['Licenças simultâneas', formData.seatCount], ['Canais incluídos no plano', selectedPlan ? String(selectedPlanIncluded) : '—'], ['Canais adicionais', formData.additionalConnectionCount], ['Cobrança', { MANUAL: 'Manual', BOLETO: 'Boleto', PIX: 'PIX', CREDIT_CARD: 'Cartão de crédito' }[createPaymentMethod]], ['Contato da empresa', [formData.email, formData.phone, formData.mobilePhone].filter(Boolean).join(' · ') || '—'], ['Endereço', [formData.addressLine1, formData.addressLine2, formData.city, formData.stateOrProvince, formData.postalCode].filter(Boolean).join(', ') || '—'], ['Período de teste', formData.enableTrial ? formData.trialDays + ' dias' : 'Sem período de teste']].map(([label, value]) => <div key={label}><dt className="text-xs text-gray-500">{label}</dt><dd className="break-words text-sm font-medium">{value}</dd></div>)}
              <div><dt className="text-xs text-gray-500">API</dt><dd className="text-sm font-medium">{selectedApiProduct ? selectedApiProduct.name + ' · ' + new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedApiProduct.price / 100) : 'Não contratada'}</dd></div>
              <div><dt className="text-xs text-gray-500">Total por período</dt><dd className="text-sm font-semibold">{selectedPlan ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: selectedPlan.currency }).format((selectedPlan.price * (Number(formData.seatCount) + Number(formData.additionalConnectionCount)) + (selectedApiProduct?.price ?? 0)) / 100) : '—'}</dd></div>
              <div><dt className="text-xs text-gray-500">Recorrência</dt><dd className="text-sm font-medium">{selectedPlan?.interval === 'year' ? 'anual' : selectedPlan?.interval === 'semester' ? 'semestral' : 'mensal'}</dd></div>
            </dl>
          </section>
</>}
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        isOpen={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedCompany(null);
          setFormData(emptyForm());
          setFormErrors({});
        }}
        title="Editar empresa"
        size="lg"
        footer={(
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => { setShowEditDialog(false); setSelectedCompany(null); setFormData(emptyForm()); setFormErrors({}); }} disabled={isSubmitting}>Cancelar</Button>
            <Button variant="primary" onClick={handleEdit} isLoading={isSubmitting}>Salvar</Button>
          </div>
        )}
      >
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Dados básicos</h3>
            <div className="space-y-3">
              <Input
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={formErrors.name}
                required
              />
              <Input
                label="Nome fantasia (opcional)"
                value={formData.fantasyName}
                onChange={(e) => setFormData({ ...formData, fantasyName: e.target.value })}
                placeholder="Nome comercial"
              />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">CNPJ e contato</h3>
            {selectedCompany?.gatewayCustomerId && (
              <p className="text-xs text-green-600 mb-2">Cliente sincronizado com Asaas. Alterações são enviadas automaticamente.</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 flex gap-2 items-end">
                <div className="flex-1">
                  <MaskedInput
                    label="CNPJ"
                    mask={MASKS.CNPJ}
                    value={formData.document}
                    onChange={(e) => setFormData((prev) => ({ ...prev, document: e.target.value }))}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={handleLookupCnpj}
                  disabled={!isCnpjFormat(formData.document)}
                  isLoading={isFetchingCnpj}
                >
                  Buscar CNPJ
                </Button>
              </div>
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <MaskedInput
                label="Telefone"
                mask={MASKS.PHONE}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <MaskedInput
                label="Celular"
                mask={MASKS.CELL}
                value={formData.mobilePhone}
                onChange={(e) => setFormData({ ...formData, mobilePhone: e.target.value })}
              />
            </div>
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MaskedInput
                  label="CEP / Postal code"
                  mask={MASKS.CEP}
                  value={formData.postalCode}
                  onChange={(e) => {
                    setFormData({ ...formData, postalCode: e.target.value });
                    if (unmask(e.target.value).length === 8) fetchAddressByCep(e.target.value);
                  }}
                />
                <Input
                  label="Address line 1"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                />
                <Input
                  label="Address line 2"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  label="Cidade"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
                <Input
                  label="Estado / Província"
                  value={formData.stateOrProvince}
                  onChange={(e) => setFormData({ ...formData, stateOrProvince: e.target.value })}
                />
                <Input
                  label="País (ISO)"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase().slice(0, 2) })}
                  placeholder="BR"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Inscrição municipal"
                  value={formData.municipalInscription}
                  onChange={(e) => setFormData({ ...formData, municipalInscription: e.target.value })}
                />
                <Input
                  label="Inscrição estadual"
                  value={formData.stateInscription}
                  onChange={(e) => setFormData({ ...formData, stateInscription: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Configurações</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Idioma padrão</label>
                <Select
                  value={formData.defaultLanguage}
                  onChange={(e) => setFormData({ ...formData, defaultLanguage: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="pt-BR">Português (Brasil)</option>
                  <option value="en-US">Inglês</option>
                  <option value="es-ES">Espanhol</option>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Fuso horário</label>
                <Select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="America/Sao_Paulo">America/São Paulo</option>
                  <option value="America/Manaus">America/Manaus</option>
                  <option value="America/Fortaleza">America/Fortaleza</option>
                  <option value="America/Recife">America/Recife</option>
                  <option value="UTC">UTC</option>
                </Select>
              </div>
            </div>
          </div>

        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        isOpen={showDeleteDialog}
        onClose={() => {
          if (!isSubmitting) {
            setShowDeleteDialog(false);
            setSelectedCompany(null);
          }
        }}
        title="Excluir empresa"
        size="md"
        footer={(
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => { setShowDeleteDialog(false); setSelectedCompany(null); }} disabled={isSubmitting}>Cancelar</Button>
            <Button variant="secondary" onClick={() => setPendingDeletionMode('PRESERVE_DATA')} disabled={isSubmitting}>Arquivar (preservar dados)</Button>
            <Button variant="danger" onClick={() => setPendingDeletionMode('DELETE_ALL_DATA')} disabled={isSubmitting}>Excluir todos os dados</Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Como deseja excluir a empresa <strong>&quot;{selectedCompany?.name}&quot;</strong>?
          </p>
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div>
              <p className="font-medium text-gray-900">Arquivar (preservar dados)</p>
              <p className="text-sm text-gray-600">
                A empresa será apenas desativada. Todos os dados (conversas, contatos, mensagens, etc.) são mantidos e a empresa pode ser restaurada depois.
              </p>
            </div>
            <div className="border-t border-gray-200 pt-3">
              <p className="font-medium text-gray-900">Excluir todos os dados</p>
              <p className="text-sm text-gray-600">
                Remove conversas, contatos, mensagens, usuários, fluxos, canais, assinaturas e dados locais de integração/cota. Mantém só o ID interno e slug técnico (casca). Cancela cobrança no Asaas quando houver vínculo. <strong>Irreversível</strong> no Orya; extratos podem permanecer no painel do gateway.
              </p>
            </div>
          </div>
        </div>
      </Dialog>

      {pendingDeletionMode && selectedCompany && (() => {
        const confirmation = companyDeletionConfirmation(pendingDeletionMode, selectedCompany.name);
        return (
          <ConfirmDialog
            isOpen
            onClose={() => { if (!isSubmitting) setPendingDeletionMode(null); }}
            onConfirm={() => void handleRequestDeletion()}
            title={confirmation.title}
            message={confirmation.message}
            confirmText={confirmation.confirmText}
            variant="danger"
            isLoading={isSubmitting}
          />
        );
      })()}

      <Dialog
        isOpen={showBulkEraseFinancialDialog}
        onClose={() => {
          if (!bulkEraseLoading) {
            setShowBulkEraseFinancialDialog(false);
            setBulkErasePhrase('');
            setBulkEraseIds([]);
          }
        }}
        title="Apagar histórico financeiro em lote"
        size="md"
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setShowBulkEraseFinancialDialog(false); setBulkErasePhrase(''); setBulkEraseIds([]); }} disabled={bulkEraseLoading}>Cancelar</Button>
            <Button variant="danger" onClick={() => void handleBulkEraseFinancialSubmit()} disabled={bulkEraseLoading || bulkErasePhrase.trim() !== 'APAGAR HISTORICO'} isLoading={bulkEraseLoading}>Executar lote</Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Serão processadas <strong>{bulkEraseIds.length}</strong> empresa(s) selecionada(s). Só entram contas com
            status <strong>PURGED</strong>; as demais aparecem como erro no resumo. Os dados não somem do gateway
            (ex.: Asaas).
          </p>
          <p className="text-sm text-gray-600">
            Para confirmar, digite em letras maiúsculas:{' '}
            <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">APAGAR HISTORICO</code>
          </p>
          <Input
            label="Confirmação"
            value={bulkErasePhrase}
            onChange={(e) => setBulkErasePhrase(e.target.value)}
            placeholder="APAGAR HISTORICO"
            autoComplete="off"
          />
        </div>
      </Dialog>

      <Dialog
        isOpen={showBulkDestroyShellDialog}
        onClose={() => {
          if (!bulkDestroyLoading) {
            setShowBulkDestroyShellDialog(false);
            setBulkDestroyPhrase('');
            setBulkDestroyIds([]);
          }
        }}
        title="Excluir shells do Orya em lote"
        size="md"
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setShowBulkDestroyShellDialog(false); setBulkDestroyPhrase(''); setBulkDestroyIds([]); }} disabled={bulkDestroyLoading}>Cancelar</Button>
            <Button variant="danger" onClick={() => void handleBulkDestroyShellSubmit()} disabled={bulkDestroyLoading || bulkDestroyPhrase.trim() !== 'EXCLUIR SEM RASTRO'} isLoading={bulkDestroyLoading}>Executar lote</Button>
          </div>
        )}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Serão processadas <strong>{bulkDestroyIds.length}</strong> empresa(s) selecionada(s). Só entram contas com
            status <strong>PURGED</strong>; as demais aparecem como erro no resumo. Remove a linha e cascatas no
            Postgres; tenta limpar blobs. <strong>Irreversível no Orya.</strong> Gateways externos não são alterados.
          </p>
          <p className="text-sm text-gray-600">
            Para confirmar, digite em letras maiúsculas:{' '}
            <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">EXCLUIR SEM RASTRO</code>
          </p>
          <Input
            label="Confirmação"
            value={bulkDestroyPhrase}
            onChange={(e) => setBulkDestroyPhrase(e.target.value)}
            placeholder="EXCLUIR SEM RASTRO"
            autoComplete="off"
          />
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={showHardDeleteDialog}
        onClose={() => {
          setShowHardDeleteDialog(false);
          setSelectedCompany(null);
        }}
        onConfirm={handleHardDelete}
        title="Excluir permanentemente (purga operacional)"
        message={`Tem certeza que deseja purgar a empresa "${selectedCompany?.name}"? Será REMOVIDO: dados operacionais (mensagens, contatos, canais, IA, mídia nos blobs app/public/manager), chaves API, webhooks, audiências e bônus de cota. Será MANTIDO no Orya: histórico de assinaturas e itens (cancelados), IDs de cliente no gateway na empresa, para arquivo e auditoria. A conta fica como shell (nome genérico, sem CNPJ/contato). A assinatura será cancelada no Asaas quando possível. Esta ação é irreversível para os dados operacionais.`}
        confirmText="Executar purga"
        cancelText="Cancelar"
        variant="danger"
        isLoading={isSubmitting}
      />

      <AlertDialog
        isOpen={showAlert}
        onClose={() => {
          setShowAlert(false);
          setAlertPaymentUrl(null);
        }}
        title="Aviso"
        message={alertMessage}
        variant="success"
        actionLabel={alertPaymentUrl ? 'Ir para pagamento' : undefined}
        onAction={alertPaymentUrl ? () => window.open(alertPaymentUrl, '_blank') : undefined}
      />

      {/* Modal de credenciais do administrador — fica visível até o staff fechar
          manualmente. A senha temporária só aparece UMA vez (backend nao
          armazena em texto). */}
      {ownerCredentials && (
        <OwnerCredentialsDialog
          credentials={ownerCredentials}
          onClose={() => setOwnerCredentials(null)}
        />
      )}
    </DashboardLayout>
  );
}

function OwnerCredentialsDialog({
  credentials,
  onClose,
}: {
  credentials: {
    companyName: string;
    email: string;
    temporaryPassword: string;
    paymentUrl: string | null;
  };
  onClose: () => void;
}) {
  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text);
  };

  const copyAll = () => {
    void navigator.clipboard.writeText(
      `Empresa: ${credentials.companyName}\nEmail: ${credentials.email}\nSenha: ${credentials.temporaryPassword}`,
    );
  };

  return (
    <Dialog isOpen onClose={onClose} closeOnOverlayClick={false} closeOnEscape={false} showCloseButton={false} title="Empresa criada — guarde estas credenciais" footer={<>
      <Button variant="secondary" size="sm" type="button" onClick={copyAll}>Copiar tudo</Button>
      {credentials.paymentUrl && <Button variant="secondary" size="sm" type="button" onClick={() => window.open(credentials.paymentUrl!, '_blank')}>Abrir pagamento</Button>}
      <Button type="button" onClick={onClose}>Já salvei, fechar</Button>
    </>}>
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
            <strong>⚠️ Atenção:</strong> a senha temporária <strong>não será exibida
            novamente</strong>. Copie agora e envie pro cliente em canal seguro.
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">Empresa</p>
              <p className="text-sm font-medium text-gray-900">{credentials.companyName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Email do administrador</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono">
                  {credentials.email}
                </code>
                <Button variant="outline" size="sm"
                  type="button"
                  onClick={() => copyToClipboard(credentials.email)}
                >
                  Copiar
                </Button>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Senha temporária</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded text-sm font-mono">
                  {credentials.temporaryPassword}
                </code>
                <Button variant="outline" size="sm"
                  type="button"
                  onClick={() => copyToClipboard(credentials.temporaryPassword)}
                >
                  Copiar
                </Button>
              </div>
            </div>
          </div>

        </div>
    </Dialog>
  );
}
